import { Button } from '@/components/buttons';
import { ContentContainer } from '@/components/layout';
import { getAnchorId, getSectionAnchorId } from '@/utils/anchors';
import { mergeClasses } from '@/utils/style';
import { motion } from 'framer-motion';
import difference from 'lodash.difference';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Image from 'next/image';
import { Link } from '@/components/links';
import { Headline, Text } from '@/components/typography';
import { LINKS } from '@/constants/links';
import { Eshop } from '@/types/eshop';
import { EshopsList } from '@/api/sanity/affiliateEshopsList';

const springAnimation = {
	type: 'spring' as const,
	stiffness: 300,
	damping: 40,
};

const EshopListsTop = ({
	eshops,
	flippingBoxesStates,
	className,
}: {
	className?: string;
	eshops: Array<{ front: Eshop; back: Eshop }>;
	flippingBoxesStates: boolean[];
}): React.JSX.Element => {
	return (
		<div
			className={mergeClasses('flex justify-between lg:absolute lg:h-full lg:flex-col', className)}
		>
			{flippingBoxesStates.length >= 1 && eshops[0] && (
				<div className="mt-8 md:mt-10 lg:mt-0 lg:ml-20 xl:ml-36">
					<FlippingBox
						isFlipped={flippingBoxesStates[0]}
						frontSide={eshops[0].front}
						backSide={eshops[0].back}
					/>
				</div>
			)}

			{flippingBoxesStates.length >= 2 && eshops[1] && (
				<div className="">
					<FlippingBox
						isFlipped={flippingBoxesStates[1]}
						frontSide={eshops[1].front}
						backSide={eshops[1].back}
					/>
				</div>
			)}

			{flippingBoxesStates.length >= 3 && eshops[2] && (
				<div className="mt-8 md:mt-10 lg:mt-0 lg:ml-24 xl:ml-42">
					<FlippingBox
						isFlipped={flippingBoxesStates[2]}
						frontSide={eshops[2].front}
						backSide={eshops[2].back}
					/>
				</div>
			)}
		</div>
	);
};

const EshopListsBottom = ({
	eshops,
	flippingBoxesStates,
	className,
}: {
	className?: string;
	eshops: Array<{ front: Eshop; back: Eshop }>;
	flippingBoxesStates: boolean[];
}): React.JSX.Element => {
	return (
		<div
			className={mergeClasses(
				'flex justify-between lg:absolute lg:top-0 lg:right-0 lg:h-full lg:flex-col lg:items-end',
				className
			)}
		>
			{flippingBoxesStates.length >= 4 && eshops[0] && (
				<div className="lg:mr-20 xl:mr-36">
					<FlippingBox
						isFlipped={flippingBoxesStates[3]}
						frontSide={eshops[0].front}
						backSide={eshops[0].back}
					/>
				</div>
			)}

			{flippingBoxesStates.length >= 5 && eshops[1] && (
				<div className="mt-8 md:mt-10 lg:mt-0">
					<FlippingBox
						isFlipped={flippingBoxesStates[4]}
						frontSide={eshops[1].front}
						backSide={eshops[1].back}
					/>
				</div>
			)}

			{flippingBoxesStates.length >= 6 && eshops[2] && (
				<div className="lg:mr-24 xl:mr-42">
					<FlippingBox
						isFlipped={flippingBoxesStates[5]}
						frontSide={eshops[2].front}
						backSide={eshops[2].back}
					/>
				</div>
			)}
		</div>
	);
};

const FlippingBox = ({
	backSide,
	frontSide,
	isFlipped,
}: {
	backSide: Eshop;
	frontSide: Eshop;
	isFlipped: boolean;
}): React.JSX.Element => {
	const className = 'absolute bg-white w-full h-full rounded-full shadow-floating';
	const linkClassname = 'flex items-center justify-center w-full h-full';
	return (
		<motion.div
			transition={springAnimation}
			className="relative h-25 w-25 md:h-30 md:w-30 lg:h-40 lg:w-40"
		>
			<motion.div
				animate={{ rotateY: isFlipped ? -180 : 0 }}
				initial={{ rotateY: 0 }}
				className={mergeClasses(className, isFlipped ? 'z-0' : 'z-10')}
				transition={springAnimation}
				style={{
					backfaceVisibility: 'hidden',
				}}
			>
				<Link
					className={linkClassname}
					analyticsParam={`homepageEshopsFront${frontSide.imageAlt.toLowerCase()}`}
					testId={`homepageEshopsFront${frontSide.imageAlt.toLowerCase()}`}
					to={frontSide.eshopLink}
					target="_blank"
				>
					<Image
						width={160}
						height={160}
						src={frontSide.imagePath}
						alt={frontSide.imageAlt}
						className="max-w-[75%]"
					/>
				</Link>
			</motion.div>
			<motion.div
				initial={{ rotateY: 180 }}
				animate={{ rotateY: isFlipped ? 0 : 180 }}
				className={mergeClasses(className, isFlipped ? 'z-10' : 'z-0')}
				transition={springAnimation}
				style={{
					backfaceVisibility: 'hidden',
				}}
			>
				<Link
					className={linkClassname}
					analyticsParam={`homepageEshopsBack${frontSide.imageAlt.toLowerCase()}`}
					testId={`homepageEshopsBack${frontSide.imageAlt.toLowerCase()}`}
					to={backSide.eshopLink}
					target="_blank"
				>
					<Image
						width={160}
						height={160}
						src={backSide.imagePath}
						alt={backSide.imageAlt}
						className="max-w-[75%]"
					/>
				</Link>
			</motion.div>
		</motion.div>
	);
};

function useFlipping(eshops: EshopsList): {
	initFlippingBoxesStates: boolean[];
	differenceRange: number[];
	topMaxSlice: number;
	bottomMaxSlice: number;
} {
	if (eshops.length % 2 !== 0) {
		throw new Error('Eshops length must be even');
	}

	return {
		initFlippingBoxesStates: new Array(eshops.length).fill(false),
		differenceRange: new Array(eshops.length).fill(0).map((_, i) => i),
		topMaxSlice: eshops.length / 2,
		bottomMaxSlice: eshops.length,
	};
}

function Eshops({
	hideText,
	title,
	text,
	button,
	className,
	eshops,
	anchorKey = 'eshops.sectionTitle',
}: {
	hideText?: boolean;
	title?: string;
	text?: string;
	button?: JSX.Element;
	className?: string;
	eshops: EshopsList;
	anchorKey?: string;
}): React.JSX.Element {
	const { t } = useTranslation('eshops');

	const { initFlippingBoxesStates, differenceRange, topMaxSlice, bottomMaxSlice } =
		useFlipping(eshops);

	const [flippingBoxesStates, setFlippingBoxesStates] =
		useState<boolean[]>(initFlippingBoxesStates);
	const [flippedBoxes, setFlippedBoxes] = useState<number[]>([]);

	useEffect(() => {
		const interval = setInterval(() => {
			if (flippedBoxes.length === flippingBoxesStates.length) {
				setFlippedBoxes([]);
			}
			let randomIndex = Math.floor(Math.random() * flippingBoxesStates.length);
			if (flippedBoxes.includes(randomIndex)) {
				randomIndex = difference(differenceRange, flippedBoxes)[0];
			}
			setFlippedBoxes((prevState) => [...prevState, randomIndex]);
			const newStates = [...flippingBoxesStates];
			newStates[randomIndex] = !flippingBoxesStates[randomIndex];
			setFlippingBoxesStates(newStates);
		}, 1000);
		return () => clearInterval(interval);
	}, [flippingBoxesStates, flippedBoxes, differenceRange]);

	const topEshops = eshops.slice(0, topMaxSlice);
	const bottomEshops = eshops.slice(topMaxSlice, bottomMaxSlice);

	return (
		<>
			<div
				id={getSectionAnchorId(anchorKey)}
				className={mergeClasses(
					'-mb-8 overflow-x-hidden bg-blue-50 px-4 pb-8 text-center md:px-[76px] lg:px-14 xl:px-0',
					className
				)}
			>
				<ContentContainer>
					<EshopListsTop
						eshops={topEshops}
						flippingBoxesStates={flippingBoxesStates}
						className="hidden lg:flex"
					/>
					<div className="w-full lg:m-auto lg:max-w-132 lg:pt-42 lg:pb-36 xl:max-w-148">
						<Headline as="h2" variant="h3" className="mb-10 lg:mb-5" id={getAnchorId(anchorKey)}>
							{title || t('sectionTitle')}
						</Headline>
						<EshopListsTop
							eshops={topEshops}
							flippingBoxesStates={flippingBoxesStates}
							className="lg:hidden"
						/>
						{!hideText && (
							<Text variant="p2" className="my-8 text-gray-800 lg:my-0">
								{text || t('sectionContent')}
							</Text>
						)}
						<EshopListsBottom
							eshops={bottomEshops}
							flippingBoxesStates={flippingBoxesStates}
							className="lg:hidden"
						/>
						{button || (
							<Button
								align="center"
								variant="primary"
								size="lg"
								to={LINKS().whereToShop}
								testId="eshopsList"
								analyticsParam="eshopsList"
								className="mt-10"
							>
								{t('sectionButton')}
							</Button>
						)}
					</div>
					<EshopListsBottom
						eshops={bottomEshops}
						flippingBoxesStates={flippingBoxesStates}
						className="hidden lg:flex"
					/>
					<div className="bgCircle absolute -right-[12rem] bottom-[70px] rotate-[-55deg] md:hidden xl:-right-[28.75rem] xl:bottom-0 xl:block xl:rotate-0" />
				</ContentContainer>
			</div>
		</>
	);
}

export default Eshops;
